import { ReactElement } from "react";
import styled, { css } from "styled-components";
import { cardSurface } from "../../../atoms/Card";

interface ImageData {
  file?: { url?: string };
  url?: string; // some EditorJS image variants store the url at the top level
  caption?: string;
  withBorder?: boolean;
  stretched?: boolean;
  withBackground?: boolean;
}

/**
 * Interprets the flags of the existing @editorjs/image tool through the design system:
 *  - stretched       -> full column width
 *  - withBackground  -> Diapason labels this toggle "Align center": centered block on a
 *                       TRANSPARENT background (never EditorJS's gray slab) — this is
 *                       what makes transparent PNGs sit directly on the page
 *  - withBorder      -> card-style border
 * Default: natural size capped at 100%, rounded corners.
 */
const Figure = styled.figure<{ $center: boolean }>`
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: ${({ $center }) => ($center ? "center" : "stretch")};
`;

const Img = styled.img<{ $stretched: boolean; $bordered: boolean }>`
  max-width: 100%;
  height: auto;
  border-radius: 16px;
  display: block;
  ${({ $stretched }) =>
    $stretched &&
    css`
      width: 100%;
      object-fit: cover;
    `}
  ${({ $bordered }) => $bordered && cardSurface}
`;

const Caption = styled.figcaption`
  font-size: 13px;
  text-align: center;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.6);
  margin-top: 6px;
`;

const ImageBlock = ({ data }: { data: ImageData }): ReactElement | null => {
  const url = data.file?.url ?? data.url;
  if (!url) return null;
  return (
    <Figure $center={!!data.withBackground}>
      <Img
        src={url}
        alt={data.caption ?? ""}
        $stretched={!!data.stretched}
        $bordered={!!data.withBorder}
      />
      {data.caption ? <Caption>{data.caption}</Caption> : null}
    </Figure>
  );
};

export default ImageBlock;
